<template>
  <teleport
    v-if="isDataInitialized"
    to="#headerPageContent"
  >
    <HeaderBarItem>
      <ProductsSwitchViewButton
        v-model="viewMode"
      />
    </HeaderBarItem>
    <HeaderBarItem>
      <HeaderSearch
        placeholder="Search devices…"
        @input="setSearchTerm"
        @change="setSearchTerm"
      />
    </HeaderBarItem>
    <HeaderBarItem>
      <DevicesFilterDropdown
        v-model="filterModel"
        :schema="filterSchema"
        :schema-options="filterSchemaOptions"
        :reset-all="resetAll"
        :apply-filters="applyFilters"
      />
    </HeaderBarItem>
  </teleport>

  <template v-if="viewMode === 'list'">
    <BaseContainer :class="$style.page">
      <TableSection
        :page="page"
        :data="devices"
        :total-items="totalDevices"
        :per-page="perPage"
        @update:page="setPage"
      >
        <template #heading>
          All lighting devices
        </template>

        <DevicesTableColumns />
      </TableSection>
    </BaseContainer>
  </template>
  <template v-else-if="viewMode === 'grid'">
    <ProductsCardsGrid has-borders>
      <ProductsDeviceCard
        v-for="device in devices"
        :key="device.id"
        :device-id="device.id"
        :type="device.type"
        :supplier-name="device.supplier?.name"
        :supplier-logo="device.supplier?.logo"
        :brand-name="device.lighting_configurations?.[0]?.car.brand.name"
        :brand-logo="device.lighting_configurations?.[0]?.car.brand.logo"
        :supplier-region="device.supplier?.region"
        :supplier-country="device.supplier?.country"
        :front-image="device.front_image"
        :car-image="device.car_image"
        :min-sop="device.min_sop"
        :max-eop="device.max_eop"
        :cars-number="device.lighting_configurations.length"
        :volume="device.volume"
      />
    </ProductsCardsGrid>
    <BaseContainer :class="$style.pagination">
      <BasePagination
        v-if="totalDevices > 0"
        :total-items="totalDevices"
        :current-page="page"
        :per-page="perPage"
        @setPage="setPage"
      />
    </BaseContainer>
  </template>
</template>

<script>
import {
  maxBy, minBy, sumBy, difference,
} from 'lodash';
import { mapState } from 'vuex';
import BaseContainer from '@/components/base/Container';
import BasePagination from '@/components/base/Pagination';
import TableSection from '@/components/TableSection';
import DevicesTableColumns from '@/components/DevicesTableColumns';
import ProductsCardsGrid from '@/components/ProductsCardsGrid';
import ProductsDeviceCard from '@/components/ProductsDeviceCard';
import HeaderBarItem from '@/components/HeaderBarItem';
import HeaderSearch from '@/components/HeaderSearch';
import ProductsSwitchViewButton from '@/components/ProductsSwitchViewButton';
import DevicesFilterDropdown from '@/components/DevicesFilterDropdown';
import lightingDevicesFilter from '@/products/lightingDevicesFilter';

export default {
  components: {
    BaseContainer,
    BasePagination,
    TableSection,
    DevicesTableColumns,
    ProductsCardsGrid,
    ProductsDeviceCard,
    HeaderBarItem,
    HeaderSearch,
    ProductsSwitchViewButton,
    DevicesFilterDropdown,
  },
  data() {
    return {
      devices: [],
      totalDevices: 0,
      viewMode: 'grid',
      perPage: 12,
      page: 1,
      searchTerm: '',
      filterModel: null,
      filterSchema: null,
      filterSchemaOptions: null,
      isDataInitialized: false,
      lastCallId: 0,
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState('app', ['carsId']),
  },
  watch: {
    filterModel: {
      deep: true,
      handler: function modelFilterChanged(prevValue, newValue) {
        const changedField = { field: null, direction: null };
        if (newValue) {
          Object.keys(newValue).filter((item) => {
            if (difference(prevValue[item], newValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'add';
            }

            if (difference(newValue[item], prevValue[item]).length !== 0) {
              changedField.field = item;
              changedField.direction = 'remove';
            }

            return true;
          });
        }
        if (changedField.field) {
          this.loadDynamicSchemaOptions(changedField);
        }
      },
    },
  },
  async created() {
    this.filterSchemaOptions = await lightingDevicesFilter.getOptions();
    this.filterSchema = await lightingDevicesFilter.getSchema();
    this.filterModel = lightingDevicesFilter.getModel();
    await this.loadPage();
    this.isDataInitialized = true;
  },
  methods: {
    setPage(page) {
      this.page = page;
      this.loadPage();
    },
    setSearchTerm(term) {
      this.searchTerm = term;
      this.setPage(1);
    },
    async loadDynamicSchemaOptions(changedField) {
      if (this.isDataInitialized) {
        this.filterSchemaOptions = {
          ...this.filterSchemaOptions,
          ...(await lightingDevicesFilter.getDynamicOptions(
            this.filterModel,
            this.searchTerm,
            changedField,
            this.carsId,
          )),
        };
      }
    },
    async loadPage() {
      this.lastCallId += 1;
      const callId = this.lastCallId;

      const res = await lightingDevicesFilter.getData(this.filterModel, this.searchTerm,
        this.perPage, this.page, this.user.company.uuid, this.carsId);

      if (this.lastCallId !== callId) return;

      this.devices = res.data.map((device) => {
        const deviceSide = device.type?.includes('HL') ? 'front' : 'back';
        let lightingConfigurations = [];
        lightingConfigurations = device.lighting_configurations.map((lc) => {
          let result = lc.car;
          result = {
            ...result,
            volumes_infos: result.volumes_infos[0],
          };
          return {
            ...lc,
            car: result,
          };
        });

        const minSopConfiguration = minBy(lightingConfigurations, 'car.volumes_infos.sop');
        const maxEopConfiguration = maxBy(lightingConfigurations, 'car.volumes_infos.eop');
        lightingConfigurations = lightingConfigurations.filter((lc) => (lc.car.volumes_infos.has_volumes
          && new Date(lc.car.volumes_infos.eop) >= new Date(2017, 0, 1)));
        const carImage = deviceSide === 'front'
          ? device.lighting_configurations?.[0]?.car.front_image
          : device.lighting_configurations?.[0]?.car.back_image;
        const volume = sumBy(device.lighting_configurations, 'take_rates_aggregate.aggregate.sum.units');

        return {
          ...device,
          min_sop: minSopConfiguration?.car.volumes_infos.sop,
          max_eop: maxEopConfiguration?.car.volumes_infos.eop,
          lighting_configurations: lightingConfigurations,
          car_image: carImage,
          volume,
        };
      }).filter((device) => device.lighting_configurations.length > 0);
      this.totalDevices = res.total;
    },
    async resetAll() {
      this.filterSchemaOptions = await lightingDevicesFilter.getOptions();
      this.filterModel = lightingDevicesFilter.getModel();
      this.setPage(1);
    },
    applyFilters() {
      this.setPage(1);
    },
  },
};
</script>

<style lang="scss" module>
.page {
  flex-grow: 1;
  padding-top: map-get($page-vertical-paddings, 'small');
  padding-bottom: map-get($page-vertical-paddings, 'small');
}

.pagination {
  margin-top: 15px;
  margin-bottom: 15px;
}
</style>
